import { useMemo } from 'react';
import { GridColDef } from '@enterprise/material-data-grid';
import i18n from '../../../../i18n';
import styles from '../ImportPage.module.scss';
import { ImportStatusCellRenderer } from '../ImportStatusCellRenderer';
import { NOT_INCLUDED_INDEX, ItemSheetRequestIndexes } from '../ImportSelectHeaders';
import { createCurrentPriceCellRenderer, importGridHeader, importRowValue, ImportGridColumn } from './useImportGridColumns.hook';
import type { ImportGridRow } from './useImportGrid.hook';

const COLUMN_MIN_WIDTH = 200;
const COLUMN_STATUS_MIN_WIDTH = 350;

const applyColumnDefaults = (columns: GridColDef<ImportGridRow>[]): GridColDef<ImportGridRow>[] =>
    columns.map((column) => ({
        minWidth: COLUMN_MIN_WIDTH,
        renderHeader: importGridHeader,
        ...column,
    }));

export interface UseAnimanaImportGridColumnsParams {
    indexes?: ItemSheetRequestIndexes | null;
    excludedFields?: string[];
}

export interface AnimanaImportGridColumnsDetails {
    animanaColumns: GridColDef<ImportGridRow>[];
}

export const useAnimanaImportGridColumns = (params: UseAnimanaImportGridColumnsParams): AnimanaImportGridColumnsDetails => {
    const { indexes, excludedFields = [] } = params;

    const animanaColumns = useMemo<GridColDef<ImportGridRow>[]>(() => {
        if (!indexes) {
            return [];
        }

        const columns: GridColDef<ImportGridRow>[] = [
            {
                field: ImportGridColumn.IdexxSapId,
                headerName: i18n.t('controlCenter:import.column.idexxSapId', 'IDEXX SAP ID') || 'IDEXX SAP ID',
                valueGetter: importRowValue((row) => row.displayable?.idexxSapId),
            },
            {
                field: ImportGridColumn.PracticeName,
                headerName: i18n.t('controlCenter:import.column.practiceName', 'Practice Name') || 'Practice Name',
                valueGetter: importRowValue((row) => row.displayable?.practiceName),
            },
        ];

        if (indexes.itemIdIndex) {
            columns.push({
                field: ImportGridColumn.ItemId,
                headerName: i18n.t('controlCenter:import.column.itemId', 'Item ID') || 'Item ID',
                valueGetter: importRowValue((row) => row.item?.pimsId),
            });
        }

        columns.push({
            field: ImportGridColumn.Description,
            minWidth: 350,
            headerName: i18n.t('controlCenter:import.column.description', 'Description') || 'Description',
            valueGetter: importRowValue((row) => row.item?.hospitalDescription),
        });

        if (indexes.itemSupplierCodeIndex) {
            columns.push({
                field: ImportGridColumn.SupplierCode,
                headerName: i18n.t('controlCenter:import.column.supplierCode', 'Supplier Code') || 'Supplier Code',
                valueGetter: importRowValue((row) => row.item?.supplierCode),
            });
        }

        if (!indexes.listPriceIndex || indexes.listPriceIndex !== NOT_INCLUDED_INDEX) {
            columns.push(
                {
                    field: ImportGridColumn.CurrentListPrice,
                    align: 'right',
                    headerName: i18n.t('controlCenter:import.column.currentListPrice', 'Current List Price') || 'Current List Price',
                    headerClassName: styles.originalPriceHeader,
                    valueGetter: importRowValue((row) => row.displayable?.currentListPrice),
                    renderCell: createCurrentPriceCellRenderer('listPrice'),
                },
                {
                    field: ImportGridColumn.NewListPrice,
                    align: 'left',
                    headerName: i18n.t('controlCenter:import.column.newListPrice', 'New List Price') || 'New List Price',
                    valueGetter: importRowValue((row) => row.configurable?.listPrice),
                },
            );
        }

        if (!indexes.costPriceIndex || indexes.costPriceIndex !== NOT_INCLUDED_INDEX) {
            columns.push(
                {
                    field: ImportGridColumn.CurrentCostPrice,
                    align: 'right',
                    headerName: i18n.t('controlCenter:import.column.currentCostPrice', 'Current Cost Price') || 'Current Cost Price',
                    headerClassName: styles.originalPriceHeader,
                    valueGetter: importRowValue((row) => row.displayable?.currentCostPrice),
                    renderCell: createCurrentPriceCellRenderer('costPrice'),
                },
                {
                    field: ImportGridColumn.NewCostPrice,
                    align: 'left',
                    headerName: i18n.t('controlCenter:import.column.newCostPrice', 'New Cost Price') || 'New Cost Price',
                    valueGetter: importRowValue((row) => row.configurable?.costPrice),
                },
            );
        }

        if (!indexes.salesPriceIndex || indexes.salesPriceIndex !== NOT_INCLUDED_INDEX) {
            columns.push(
                {
                    field: ImportGridColumn.CurrentSalesPrice,
                    align: 'right',
                    headerName: i18n.t('controlCenter:import.column.currentSalesPrice', 'Current Sales Price') || 'Current Sales Price',
                    headerClassName: styles.originalPriceHeader,
                    valueGetter: importRowValue((row) => row.displayable?.currentSalesPrice),
                    renderCell: createCurrentPriceCellRenderer('salesPrice'),
                },
                {
                    field: ImportGridColumn.NewSalesPrice,
                    align: 'left',
                    headerName: i18n.t('controlCenter:import.column.newSalesPrice', 'New Sales Price') || 'New Sales Price',
                    valueGetter: importRowValue((row) => row.configurable?.salesPrice),
                },
            );
        }

        columns.push(
            {
                field: ImportGridColumn.ClassDescription,
                headerName: i18n.t('controlCenter:import.column.classDescription', 'Class Description') || 'Class Description',
                valueGetter: importRowValue((row) => row.item?.classification?.description),
            },
            {
                field: ImportGridColumn.Status,
                headerName: i18n.t('controlCenter:import.column.status', 'Status'),
                minWidth: COLUMN_STATUS_MIN_WIDTH,
                flex: 1,
                valueGetter: importRowValue((row) => row.item?.status?.message),
                cellClassName: styles.statusCell,
                renderCell: ImportStatusCellRenderer,
            },
        );

        return applyColumnDefaults(columns).filter(({ field }) => !excludedFields.includes(field as string));
    }, [indexes, excludedFields]);

    return { animanaColumns };
};
